import type { PostFeedbackAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import type { PostFeedbackRating } from '@/client/services/worlds'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import PostFeedbackFeaturedQ from './PostFeedbackFeaturedQ'
import PostQ from './PostQ'

const PostFeedbackQ = {
  ...makeQueryModelV2<PostFeedbackAttributes>('post_feedback')([
    'id',
    'user_id',
    'reviewer_user_id',
    'post_id',
    'rating',
    'note',
    'internal_note',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Post: {
      query: PostQ,
      foreignKey: PostQ.id,
      sourceKey: PostFeedbackQ.post_id,
    },
  }),

  where: (
    {
      id,
      userId,
      reviewerUserId,
      postId,
      rating,
      groupBy = null,
      include = [],
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      reviewerUserId?: number | null | SQLFragment<number>
      postId?: number | null | SQLFragment<number>
      rating?: PostFeedbackRating | null | SQLFragment<PostFeedbackRating>
      groupBy?: GroupBy | null
      include?: IncludeAssociations
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<PostFeedbackAttributes> => SQL`
        FROM ${PostFeedbackQ}
        ${includeAssociationsToSql(PostFeedbackQ, include)}
        WHERE
            ${id != null ? SQL`${PostFeedbackQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${PostFeedbackQ.user_id} = ${userId} AND` : SQL``}
            ${
              reviewerUserId != null
                ? SQL`${PostFeedbackQ.reviewer_user_id} = ${reviewerUserId} AND`
                : SQL``
            }
            ${postId != null ? SQL`${PostFeedbackQ.post_id} = ${postId} AND` : SQL``}
            ${rating != null ? SQL`${PostFeedbackQ.rating} = ${rating} AND` : SQL``}
            ${paranoid ? SQL`${PostFeedbackQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  isFeatured: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
        EXISTS(
            SELECT 1 ${PostFeedbackFeaturedQ.where({
              postFeedbackId: PostFeedbackQ.id,
              userId,
            })}
        )
    `,
}

export default PostFeedbackQ
